import type { CreateCommentBody } from "@nlc/shared-types";

import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { attachmentsKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

interface UseCreateChatCommentProps {
  requestId: number;
}

interface CreateChatCommentParams {
  attachments?: Array<{
    attachmentId: number;
    name: string;
  }>;
  comment: string;
}

export function useCreateChatComment({
  requestId,
}: UseCreateChatCommentProps): UseMutationResult<
  Response,
  Error,
  CreateChatCommentParams
> {
  const { data: user } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      attachments = [],
      comment,
    }: CreateChatCommentParams) => {
      if (!user?.user.id) {
        throw new Error("User not authenticated");
      }

      const body: CreateCommentBody = {
        attachments,
        comment,
        parentId: requestId,
        userId: user.user.id,
      };

      return apiClient.url(`/stories/chat/${requestId}`).post(body).res();
    },
    onError: (error: Error) => {
      console.error("Failed to create chat comment:", error);

      toast.add(
        {
          description: error.message || "Please try again later",
          title: "Error sending message",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: attachmentsKeys.attachmentsByRequestId(requestId),
      });
    },
  });
}
